import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { LeaseDTO } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatCents } from '../../lib/money';
import { formatDate } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';

/** Open a document by minting a short-lived signed URL, then navigating to it. */
async function openDocument(id: string): Promise<void> {
  const res = await api.documents.signedUrl(id);
  window.open(res.url, '_blank', 'noopener');
}

export function LeaseView({ lease }: { lease: LeaseDTO | null }) {
  const [downloadError, setDownloadError] = useState<string | null>(null);

  if (!lease) {
    return (
      <>
        <h1>Your lease</h1>
        <div className="card">
          <p>You do not have an active lease on file.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <h1>Your lease</h1>
      <p className="subtitle">
        Status: <span className="badge">{lease.status}</span>
      </p>

      {downloadError && <StatusMessage tone="err">{downloadError}</StatusMessage>}

      <section aria-labelledby="terms-heading">
        <h2 id="terms-heading">Terms</h2>
        <div className="card">
          <dl className="stack" style={{ margin: 0 }}>
            <div className="row">
              <dt style={{ fontWeight: 600, minWidth: 160 }}>Term</dt>
              <dd style={{ margin: 0 }}>
                {lease.termType === 'fixed' ? 'Fixed term' : 'Month-to-month'}
              </dd>
            </div>
            <div className="row">
              <dt style={{ fontWeight: 600, minWidth: 160 }}>Start date</dt>
              <dd style={{ margin: 0 }}>{formatDate(lease.startDate)}</dd>
            </div>
            <div className="row">
              <dt style={{ fontWeight: 600, minWidth: 160 }}>End date</dt>
              <dd style={{ margin: 0 }}>{lease.endDate ? formatDate(lease.endDate) : '—'}</dd>
            </div>
            <div className="row">
              <dt style={{ fontWeight: 600, minWidth: 160 }}>Monthly rent</dt>
              <dd style={{ margin: 0 }}>{formatCents(lease.rentCents)}</dd>
            </div>
            <div className="row">
              <dt style={{ fontWeight: 600, minWidth: 160 }}>Security deposit</dt>
              <dd style={{ margin: 0 }}>
                {lease.depositCents != null ? formatCents(lease.depositCents) : '—'}
              </dd>
            </div>
            <div className="row">
              <dt style={{ fontWeight: 600, minWidth: 160 }}>Move-in date</dt>
              <dd style={{ margin: 0 }}>{formatDate(lease.moveInDate)}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section aria-labelledby="schedule-heading">
        <h2 id="schedule-heading">Payment schedule</h2>
        {lease.schedule && lease.schedule.installments.length > 0 ? (
          <div className="table-wrap card" style={{ padding: 0 }}>
            <table>
              <caption className="visually-hidden">Rent installment schedule</caption>
              <thead>
                <tr>
                  <th scope="col">Installment</th>
                  <th scope="col">Day of month</th>
                  <th scope="col" className="num">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {lease.schedule.installments.map((inst) => (
                  <tr key={inst.id}>
                    <td>{inst.label ?? '—'}</td>
                    <td>{inst.dayOfMonth}</td>
                    <td className="num">{formatCents(inst.amountCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">No installment schedule is defined.</p>
        )}
      </section>

      <section aria-labelledby="doc-heading">
        <h2 id="doc-heading">Executed lease document</h2>
        {lease.executedDocumentId ? (
          <button
            type="button"
            onClick={async () => {
              setDownloadError(null);
              try {
                await openDocument(lease.executedDocumentId!);
              } catch (err) {
                setDownloadError(errorMessage(err));
              }
            }}
          >
            Open executed lease (PDF)
          </button>
        ) : (
          <p className="muted">The executed lease document has not been uploaded yet.</p>
        )}
      </section>
    </>
  );
}

export function Lease() {
  const query = useQuery({ queryKey: ['portal', 'lease'], queryFn: () => api.portal.lease() });
  if (query.isLoading) return <Loading label="Loading your lease…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;
  return <LeaseView lease={query.data.lease} />;
}
